import * as React from "react";
import { BookOpen, Gauge, RefreshCw } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { Text } from "../../../ui/components/Text";
import { Callout } from "../../../ui/components/Callout";
import { ListView } from "../../../ui/components/ListView";
import { StatusBadge } from "../../../ui/components/StatusBadge";
import type { ColumnDefinition } from "../../../ui/components/Table/types";
import type { ListParams } from "../../../ui/api/listApi.types";
import { useAuth } from "../../lib/auth/hooks";
import { useI18n, type TranslateValues } from "../../lib/i18n";
import {
  fetchSupportRequests,
  type InsightSupportImpact,
  type SupportInboxRow,
  type SupportInboxStatus,
} from "../../lib/api/insights";
import { SupportRequestDetailModal } from "./SupportRequestDetailModal";
import * as pageStyles from "../users/UsersListPage.css";

type T = (key: string, values?: TranslateValues) => string;

export const IMPACT_BADGE: Record<InsightSupportImpact, "fault" | "pending" | "online"> = {
  down: "fault",
  degraded: "pending",
  working: "online",
};

function statusFromFilters(filters: Record<string, unknown> | undefined): SupportInboxStatus {
  const raw = filters?.status;
  const selected = Array.isArray(raw) ? raw.map(String) : [];
  if (selected.length === 1 && (selected[0] === "open" || selected[0] === "handled")) return selected[0];
  return "all";
}

function columns(t: T): ColumnDefinition<SupportInboxRow>[] {
  return [
    {
      id: "reference",
      label: t("pro.system.insights.support.col.reference"),
      pinnedLeft: true,
      render: (row) => <span style={{ fontFamily: "monospace", fontWeight: 600 }}>{row.reference}</span>,
    },
    {
      id: "createdAt",
      label: t("pro.system.insights.support.col.received"),
      sortable: true,
      render: (row) => new Date(row.createdAt).toLocaleString(),
    },
    {
      id: "impact",
      label: t("pro.system.insights.support.col.impact"),
      render: (row) =>
        row.impact ? (
          <StatusBadge
            size="sm"
            variant={IMPACT_BADGE[row.impact]}
            label={t(`pro.system.insights.support.impact.${row.impact}`)}
          />
        ) : (
          "—"
        ),
    },
    {
      id: "product",
      label: t("pro.system.insights.support.col.product"),
      render: (row) => row.productName ?? row.productId,
    },
    {
      id: "subject",
      label: t("pro.system.insights.support.col.subject"),
      render: (row) =>
        row.insightTitle ?? (row.alertCode ? t("pro.system.insights.support.alertOnly", { code: row.alertCode }) : "—"),
    },
    {
      id: "requester",
      label: t("pro.system.insights.support.col.requester"),
      render: (row) => row.requestedByName ?? row.requestedByEmail ?? row.requestedByUserId,
    },
    {
      id: "status",
      label: t("pro.system.insights.support.col.status"),
      filterType: "checkbox",
      filterOptions: [
        { value: "open", label: t("pro.system.insights.support.status.open") },
        { value: "handled", label: t("pro.system.insights.support.status.handled") },
      ],
      render: (row) =>
        row.handledAt ? (
          <StatusBadge size="sm" variant="enrolled" label={t("pro.system.insights.support.status.handled")} />
        ) : (
          <StatusBadge size="sm" variant="pending" label={t("pro.system.insights.support.status.open")} />
        ),
    },
  ];
}

export function SupportInboxPage({ initialReference }: { initialReference?: string }) {
  const { t } = useI18n();
  const { accessToken, user } = useAuth();
  const navigate = useNavigate();
  const token = accessToken ?? localStorage.getItem("access_token");
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [selected, setSelected] = React.useState<string | null>(initialReference ?? null);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  const cols = React.useMemo(() => columns(t), [t]);

  const fetchList = React.useCallback(
    async (params: ListParams) => {
      if (!token) return { data: [] as SupportInboxRow[], total: 0 };
      try {
        const res = await fetchSupportRequests(token, {
          status: statusFromFilters(params.filters),
          search: params.search,
          page: Math.max(1, params.page || 1),
          limit: Math.max(1, params.limit || 25),
          order: params.order === "asc" ? "asc" : "desc",
        });
        setLoadError(null);
        return res;
      } catch (err) {
        setLoadError(err instanceof Error ? err.message : t("pro.system.insights.support.loadFailed"));
        return { data: [], total: 0 };
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, refreshKey, t],
  );

  const headerActions = React.useMemo(
    () => [
      {
        id: "usage",
        label: t("pro.system.insights.support.usageLink"),
        icon: <Gauge size={14} />,
        color: "neutral" as const,
        variant: "outline" as const,
        onClick: () => navigate({ to: "/system/insights" }),
      },
      {
        id: "guidance",
        label: t("pro.system.insights.guidanceLink"),
        icon: <BookOpen size={14} />,
        color: "neutral" as const,
        variant: "outline" as const,
        onClick: () => navigate({ to: "/system/insights/guidance" }),
      },
      {
        id: "refresh",
        label: t("pro.system.insights.refresh"),
        icon: <RefreshCw size={14} />,
        color: "neutral" as const,
        variant: "outline" as const,
        onClick: () => setRefreshKey((k) => k + 1),
      },
    ],
    [navigate, t],
  );

  return (
    <div className={pageStyles.page}>
      <div className={pageStyles.pageHeader}>
        <Text level="h1" color="text" weight="bold">
          {t("pro.system.insights.support.title")}
        </Text>
      </div>

      {loadError ? (
        <div className={pageStyles.errorWrap}>
          <Callout variant="error">{loadError}</Callout>
        </div>
      ) : null}

      <div className={pageStyles.tableHost}>
        <ListView<SupportInboxRow>
          listId="system-insights-support"
          userId={user?.id ?? "anonymous"}
          columns={cols}
          fetchList={fetchList}
          contextKey={String(refreshKey)}
          fillContainer
          enabledViews={["table"]}
          initialColumnFilters={[{ id: "status", value: ["open"] }]}
          defaultSort={{ sort_by: "createdAt", order: "desc" }}
          searchPlaceholder={t("pro.system.insights.support.search")}
          emptyTitle={t("pro.system.insights.support.emptyTitle")}
          emptySubtitle={t("pro.system.insights.support.emptySubtitle")}
          onRowClick={(row) => setSelected(row.id)}
          headerActions={headerActions}
        />
      </div>

      {selected ? (
        <SupportRequestDetailModal
          idOrReference={selected}
          token={token}
          onClose={() => setSelected(null)}
          onChanged={() => setRefreshKey((k) => k + 1)}
        />
      ) : null}
    </div>
  );
}
